/**
 * BusquedaAvanzadaSheet - Panel lateral de búsqueda avanzada
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useBusquedaAvanzada, type BusquedaParams } from "@/hooks/use-busqueda-avanzada";
import { cleanSearchParams, type BusquedaFormData } from "../utils/search-validation";
import { SearchForm } from "./search-form";
import { SearchResults } from "./search-results";

interface BusquedaAvanzadaSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialParams?: Partial<BusquedaParams>;
  autoSearch?: boolean;
}

export function BusquedaAvanzadaSheet({
  open,
  onOpenChange,
  initialParams,
  autoSearch = false,
}: BusquedaAvanzadaSheetProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState<BusquedaParams | null>(null);
  const [page, setPage] = useState(1);
  const abortRef = useRef<AbortController | null>(null);
  const autoSearchDone = useRef(false);

  const paramsWithPage = searchParams ? { ...searchParams, page, page_size: 10 } : null;
  const { data, isLoading, error, refetch } = useBusquedaAvanzada(paramsWithPage);

  const handleSearch = useCallback((formData: BusquedaFormData) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setPage(1);
    setSearchParams(cleanSearchParams(formData));
  }, []);

  const handleClear = useCallback(() => {
    setSearchParams(null);
    setPage(1);
  }, []);

  // Auto-search when opened with initialParams
  useEffect(() => {
    if (open && autoSearch && initialParams && !autoSearchDone.current) {
      autoSearchDone.current = true;
      const cleaned = cleanSearchParams(initialParams as BusquedaFormData);
      if (Object.keys(cleaned).length > 0) {
        setPage(1);
        setSearchParams(cleaned);
      }
    }
    if (!open) {
      autoSearchDone.current = false;
    }
  }, [open, autoSearch, initialParams]);

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      setSearchParams(null);
      setPage(1);
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] flex flex-col p-0"
        role="search"
        aria-label={t("busquedaAvanzada.title")}
      >
        <SheetHeader className="px-4 pt-4 pb-0">
          <SheetTitle>{t("busquedaAvanzada.title")}</SheetTitle>
          <SheetDescription>{t("busquedaAvanzada.description")}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="px-4 py-3 border-b">
            <SearchForm
              onSearch={handleSearch}
              onClear={handleClear}
              isLoading={isLoading}
              initialValues={initialParams}
              autoFocus={open}
            />
          </div>

          <div className="flex-1 overflow-y-auto" aria-live="polite">
            <SearchResults
              data={data ?? null}
              isLoading={isLoading}
              error={error}
              hasSearched={searchParams !== null}
              page={page}
              onPageChange={setPage}
              onRetry={() => refetch()}
              onCloseSheet={() => onOpenChange(false)}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
